import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function Schedule() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    holidays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [savedHolidays, setSavedHolidays] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [notification, setNotification] = useState(null);

  // Fetch existing schedules function
  const fetchSchedules = async () => {
    if (!schoolData?.id || !selectedBranchId) {
      setLoadingSchedules(false);
      return;
    }
    
    try {
      let query = supabase
        .from('schedules')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('branch_id', selectedBranchId);
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching schedules:', error);
        return;
      }
      
      setSchedules(data || []);
      
      // Load the most recent saved holidays if they exist
      if (data && data.length > 0) {
        const mostRecentSchedule = data[0];
        if (mostRecentSchedule.holidays) {
          setSavedHolidays(mostRecentSchedule.holidays);
          // Also populate the form with saved holidays for editing
          setScheduleForm({
            holidays: { ...mostRecentSchedule.holidays }
          });
          console.log('Loaded saved holidays from database:', mostRecentSchedule.holidays);
          console.log('Schedule ID:', mostRecentSchedule.id);
        } else {
          setSavedHolidays(null);
        }
      } else {
        console.log('No existing schedules found for branch:', selectedBranchId);
        setSavedHolidays(null);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingSchedules(false);
    }
  };

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  // Add a refresh function to reload data when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && schoolData?.id) {
        // Page became visible, refresh the data
        console.log('Page became visible, refreshing schedule data...');
        fetchSchedules();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id) {
      // Reset saved holidays when branch changes
      setSavedHolidays(null);
      setIsEditing(false);
      setScheduleForm({
        holidays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        }
      });
      // Refetch schedules for the selected branch
      fetchSchedules();
    }
  }, [schoolData?.id, selectedBranchId]);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Schedule form functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setScheduleForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleHolidayChange = (day) => {
    setScheduleForm(prev => ({
      ...prev,
      holidays: {
        ...prev.holidays,
        [day]: !prev.holidays[day]
      }
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    // No validation needed for simplified form
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedBranchId) {
      setNotification({ type: 'error', message: 'Please select a branch first before creating a holiday schedule.' });
      setTimeout(() => setNotification(null), 4000);
      return;
    }
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const scheduleData = {
        school_id: schoolData.id,
        branch_id: selectedBranchId,
        schedule_name: 'Holiday Schedule',
        academic_year: new Date().getFullYear().toString(),
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        holidays: scheduleForm.holidays,
        description: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('schedules')
        .insert(scheduleData)
        .select()
        .single();
      
      if (error) {
        console.error('Error creating schedule:', error);
        console.error('Error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          scheduleData: scheduleData
        });
        
        let errorMessage = 'Failed to create schedule. Please try again.';
        if (error.code === '42501' || error.message?.includes('permission denied') || error.message?.includes('403')) {
          errorMessage = 'Permission denied. Please ensure you are properly authenticated. If the issue persists, contact your administrator.';
        } else if (error.message) {
          errorMessage = `Error: ${error.message}`;
        }
        
        setNotification({ type: 'error', message: errorMessage });
        setTimeout(() => setNotification(null), 6000);
        return;
      }
      
      // Update local state
      setSchedules(prev => [data, ...prev]);
      
      // Save the created holidays to display
      setSavedHolidays(scheduleForm.holidays);
      
      console.log('Created new schedule with holidays:', scheduleForm.holidays);
      console.log('Schedule data:', data);
      
      // Reset form
      setScheduleForm({
        holidays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        }
      });
      setFormErrors({});
      
      setNotification({ type: 'success', message: 'Holiday schedule created successfully!' });
      setTimeout(() => setNotification(null), 4000);
      
    } catch (err) {
      console.error('Error:', err);
      setNotification({ type: 'error', message: 'Failed to create holiday schedule. Please try again.' });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getHolidayDays = (holidays) => {
    if (!holidays) return 'No holidays';
    const holidayDays = Object.entries(holidays)
      .filter(([day, isHoliday]) => isHoliday)
      .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1));
    return holidayDays.length > 0 ? holidayDays.join(', ') : 'No holidays';
  };

  const handleEditHolidays = () => {
    setIsEditing(true);
    setScheduleForm({
      holidays: { ...savedHolidays }
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setScheduleForm({
      holidays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
      }
    });
  };

  const handleUpdateHolidays = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Find the most recent schedule to update
      if (schedules.length > 0) {
        const mostRecentSchedule = schedules[0];
        
        const updateData = {
          holidays: scheduleForm.holidays,
          updated_at: new Date().toISOString()
        };
        
        const { error } = await supabase
          .from('schedules')
          .update(updateData)
          .eq('id', mostRecentSchedule.id);
        
        if (error) {
          console.error('Error updating schedule:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          
          let errorMessage = 'Failed to update holidays. Please try again.';
          if (error.code === '42501' || error.message?.includes('permission denied') || error.message?.includes('403')) {
            errorMessage = 'Permission denied. Please ensure you are properly authenticated. If the issue persists, contact your administrator.';
          } else if (error.message) {
            errorMessage = `Error: ${error.message}`;
          }
          
          setNotification({ type: 'error', message: errorMessage });
          setTimeout(() => setNotification(null), 6000);
          return;
        }
        
        // Update local state
        setSchedules(prev => prev.map(schedule => 
          schedule.id === mostRecentSchedule.id 
            ? { ...schedule, ...updateData }
            : schedule
        ));
        
        // Update the saved holidays
        setSavedHolidays(scheduleForm.holidays);
        
        console.log('Updated holidays in database:', updateData);
      } else {
        // If no schedules exist, create a new one
        const scheduleData = {
          school_id: schoolData.id,
          branch_id: selectedBranchId,
          schedule_name: 'Holiday Schedule',
          academic_year: new Date().getFullYear().toString(),
          start_date: new Date().toISOString().split('T')[0],
          end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          holidays: scheduleForm.holidays,
          description: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const { data, error } = await supabase
          .from('schedules')
          .insert(scheduleData)
          .select()
          .single();
        
        if (error) {
          console.error('Error creating schedule:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint
          });
          
          let errorMessage = 'Failed to create schedule. Please try again.';
          if (error.code === '42501' || error.message?.includes('permission denied') || error.message?.includes('403')) {
            errorMessage = 'Permission denied. Please ensure you are properly authenticated. If the issue persists, contact your administrator.';
          } else if (error.message) {
            errorMessage = `Error: ${error.message}`;
          }
          
          setNotification({ type: 'error', message: errorMessage });
          setTimeout(() => setNotification(null), 6000);
          return;
        }
        
        // Update local state
        setSchedules(prev => [data, ...prev]);
        setSavedHolidays(scheduleForm.holidays);
      }
      
      // Reset form
      setScheduleForm({
        holidays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        }
      });
      setFormErrors({});
      setIsEditing(false);
      
      setNotification({ type: 'success', message: 'Holidays updated successfully!' });
      setTimeout(() => setNotification(null), 4000);
      
    } catch (err) {
      console.error('Error:', err);
      setNotification({ type: 'error', message: 'Failed to update holidays. Please try again.' });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="schedule-content">
          <div className="schedule-header">
            <div>
              <h1>Create Schedule</h1>
              <p>Set up academic schedules and mark holidays for your school</p>
            </div>
          </div>

          {/* Branch Selector Section */}
          <div className="dashboard-branch-row schedule-view-branch-row" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
            <BranchSelector
              schoolId={schoolData?.id}
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
            <div className="branch-context" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>
              {selectedBranchId 
                ? `📍 Showing holiday schedules for the selected branch` 
                : '⚠️ Please select a branch to view and manage holiday schedules'}
            </div>
          </div>


          {/* Show message if no branch is selected */}
          {!selectedBranchId ? (
            <div className="add-schedule-section">
              <div className="add-schedule-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📍</div>
                <h2 style={{ marginBottom: '1rem', color: '#374151' }}>Select a Branch</h2>
                <p style={{ color: '#6B7280', fontSize: '1.1rem', lineHeight: '1.6' }}>
                  Please select a branch from the dropdown above to view and manage holiday schedules for that branch.
                </p>
              </div>
            </div>
          ) : loadingSchedules ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading saved holidays...</p>
            </div>
          ) : (
            <>
              {/* Show Saved Holidays View if holidays exist and not editing */}
              {savedHolidays && !isEditing ? (
                <div className="saved-holidays-section">
                  <div className="saved-holidays-card">
                    <h2>✅ Saved Holidays</h2>
                    <div className="saved-holidays-content">
                      <div className="holidays-display">
                        <h3>Your Selected Holidays:</h3>
                        <div className="holidays-list">
                          {Object.entries(savedHolidays).map(([day, isHoliday]) => (
                            <div key={day} className={`holiday-item ${isHoliday ? 'selected' : 'not-selected'}`}>
                              <span className="holiday-day">
                                {day.charAt(0).toUpperCase() + day.slice(1)}
                              </span>
                              <span className="holiday-status">
                                {isHoliday ? '✅ Holiday' : 'Working Day'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="holidays-actions" style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #E5E7EB', display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <button 
                          type="button" 
                          className="edit-btn"
                          onClick={handleEditHolidays}
                          style={{
                            background: 'linear-gradient(135deg, #FF8C42, #E67A35)',
                            color: 'white',
                            border: 'none',
                            padding: '1rem 2.5rem',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 4px 12px rgba(255, 140, 66, 0.35)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '180px',
                            letterSpacing: '0.3px',
                            textTransform: 'none',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #E67A35, #D2691E)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 18px rgba(255, 140, 66, 0.45)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #FF8C42, #E67A35)';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 140, 66, 0.35)';
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                        >
                          Edit Holidays
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Show Create/Edit Form if no holidays exist OR if editing */
                <div className="add-schedule-section">
                  <div className="add-schedule-card">
                    <h2>{isEditing ? '✏️ Edit Holidays' : '📅 Create Holiday Schedule'}</h2>
                    <form onSubmit={isEditing ? handleUpdateHolidays : handleSubmit} className="add-schedule-form">
                      <div className="form-section">
                        <h3>Holiday Selection</h3>
                        <p className="form-help">Select the days that are holidays (no classes will be held on these days)</p>
                        
                        <div className="holidays-grid">
                          {Object.entries(scheduleForm.holidays).map(([day, isHoliday]) => (
                            <div key={day} className="holiday-checkbox-item">
                              <label className="holiday-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={isHoliday}
                                  onChange={() => handleHolidayChange(day)}
                                />
                                <span className="holiday-checkbox-text">
                                  {day.charAt(0).toUpperCase() + day.slice(1)}
                                </span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="form-actions">
                        {isEditing ? (
                          <>
                            <button type="button" className="cancel-btn" onClick={handleCancelEdit}>
                              Cancel Edit
                            </button>
                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                              {isSubmitting ? 'Updating...' : 'Update Holidays'}
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="cancel-btn" onClick={() => {
                              setScheduleForm({
                                holidays: {
                                  monday: false,
                                  tuesday: false,
                                  wednesday: false,
                                  thursday: false,
                                  friday: false,
                                  saturday: false,
                                  sunday: false
                                }
                              });
                              setFormErrors({});
                            }}>
                              Clear Form
                            </button>
                            <button type="submit" className="submit-btn" disabled={isSubmitting}>
                              {isSubmitting ? 'Creating...' : 'Create Holiday Schedule'}
                            </button>
                          </>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Success/Error Notification */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10000,
            minWidth: '300px',
            maxWidth: '500px',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            backgroundColor: notification.type === 'success' ? '#10B981' : '#EF4444',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            animation: 'slideInRight 0.3s ease-out',
            borderLeft: `4px solid ${notification.type === 'success' ? '#059669' : '#DC2626'}`,
          }}
        >
          <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
            {notification.type === 'success' ? '✓' : '✕'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>
              {notification.type === 'success' ? 'Success!' : 'Error!'}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>
              {notification.message}
            </div>
          </div>
          <button
            onClick={() => setNotification(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default Schedule;
